<template>
	<header
		id="siteHeader"
		:class="{ scrolled: isScrolled }"
	>
		<button
			class="logo-mark"
			aria-label="Ir para a Home"
			@click="goHome"
		>
			<img
				src="../../assets/images/LOGO-ABDC-WHITE.png"
				alt="Angry Bird Diners Club"
			/>
		</button>

		<BurgerMenu
			:active="navOpen"
			@toggle="navOpen = !navOpen"
		/>

		<nav :class="{ open: navOpen }">
			<a
				href="https://www.angrybirddinersclub.io/qs-secret-club/"
				class="nav-link"
				@click="onSecretClubClick"
				>Q's Secret Club</a
			>
			<button
				type="button"
				class="nav-link early-worm-link"
				@click="openEarlyWorm"
			>
				Early Worm
			</button>
			<router-link
				to="/manifesto"
				class="nav-link"
				:class="{ active: isManifesto }"
				@click="navOpen = false"
				>Manifesto</router-link
			>
			<div class="nav-socials">
				<a
					href="https://x.com/AngryBirdDC_"
					target="_blank"
					rel="noopener"
					aria-label="X (Twitter)"
				>
					<svg
						viewBox="0 0 24 24"
						fill="currentColor"
						style="color: var(--ink)"
					>
						<path
							d="M18.9 2H22l-7.7 8.8L23.3 22h-7.1l-5.6-6.9L4.1 22H1l8.3-9.4L1 2h7.3l5 6.3L18.9 2zm-1.2 18h1.9L7.4 4h-2l12.3 16z"
						/>
					</svg>
				</a>
				<span
					class="social-disabled"
					data-cursor-soon
					aria-label="Discord (em breve)"
					aria-disabled="true"
				>
					<img
						src="../../assets/images/DISCORD-ICON-BRANCO.png"
						alt="Discord"
					/>
				</span>
			</div>
		</nav>
	</header>

	<div
		class="portal-overlay"
		:class="{ active: portalActive, expand: portalExpand, exiting: portalExiting }"
		:style="portalStyle"
	>
		<div class="portal-backdrop"></div>
		<div class="portal-content">
			<div class="portal-ring-wrap">
				<svg
					class="portal-ring-svg"
					viewBox="0 0 120 120"
				>
					<circle
						class="ring-track"
						cx="60"
						cy="60"
						r="52"
					></circle>
					<circle
						class="ring-progress"
						:class="{ granted: portalGranted }"
						cx="60"
						cy="60"
						r="52"
						:style="{ strokeDashoffset: portalDashOffset }"
					></circle>
				</svg>
				<span class="portal-percent">{{ portalPercent }}%</span>
			</div>
			<p
				class="portal-status"
				:class="{ granted: portalGranted }"
			>
				{{ portalGranted ? 'ACCESS GRANTED' : 'VERIFYING ACCESS' }}
			</p>
		</div>
		<div class="portal-circle"></div>
	</div>

	<!-- ===== Early Worm — Modal 1: explicação antes de conectar ===== -->
	<div
		class="ew-overlay"
		:class="{ show: ewModal === 'intro' }"
	>
		<div
			class="ew-backdrop"
			@click="closeEarlyWorm"
		></div>
		<div class="ew-modal">
			<button
				class="ew-close"
				aria-label="Fechar"
				@click="closeEarlyWorm"
			>
				✕
			</button>
			<div class="ew-icon-row"><span class="dot"></span><span>Early Worm Whitelist</span></div>
			<h2>Get In Early</h2>
			<p>
				Connect your Solana wallet to check mint eligibility status. You need to hold <b>$QUILL</b> to confirm for
				the Early Worm whitelist.
			</p>
			<p class="ew-fineprint">
				If your $QUILL balance drops below the required threshold before the final snapshot, your Early Worm
				confirmation may be voided.
			</p>
			<button
				class="ew-btn"
				:disabled="earlyWormBusy"
				@click="onConnectWallet"
			>
				<img
					class="quill-icon-buy-img"
					src="../../assets/images/QUILL-ICON-BLACK.png"
					alt=""
				/>
				{{ connectLabel }}
			</button>
		</div>
	</div>

	<!-- ===== Early Worm — Modal 2: sucesso na verificação ===== -->
	<div
		class="ew-overlay"
		:class="{ show: ewModal === 'success' }"
	>
		<div
			class="ew-backdrop"
			@click="closeEarlyWorm"
		></div>
		<div
			class="ew-modal"
			:class="{ whitelisted: ewAlreadyRegistered }"
		>
			<button
				class="ew-close"
				aria-label="Fechar"
				@click="closeEarlyWorm"
			>
				✕
			</button>
			<template v-if="ewAlreadyRegistered">
				<div class="ew-icon-box">
					<!-- shield-check icon -->
					<svg
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						stroke-width="2"
						stroke-linecap="round"
						stroke-linejoin="round"
					>
						<path d="M12 2l8 4v6c0 5-3.4 8.5-8 10-4.6-1.5-8-5-8-10V6l8-4z" />
						<path d="M9 12l2 2 4-4" />
					</svg>
				</div>
				<div class="ew-icon-row"><span>🛡</span><span>Whitelisted ✓</span></div>
				<h2>You're Already In</h2>
				<span class="secured-chip">
					<svg
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						stroke-width="2.5"
						stroke-linecap="round"
						stroke-linejoin="round"
					>
						<path d="M20 6L9 17l-5-5" />
					</svg>
					WALLET SECURED
				</span>
				<p>This wallet is already on the Early Worm whitelist. No action needed.</p>
				<p>Follow <b>@AngryBirdDC_</b> on X for mint date reveal &amp; brand updates.</p>
			</template>
			<template v-else>
				<div class="ew-icon-box">
					<svg
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						stroke-width="2"
						stroke-linecap="round"
						stroke-linejoin="round"
					>
						<path d="M20 6L9 17l-5-5" />
					</svg>
				</div>
				<div class="ew-icon-row"><span class="dot"></span><span>Whitelist Confirmed</span></div>
				<h2>Congrats Familia, You're In</h2>
				<p>This wallet has been added to the Early Worm whitelist.</p>
				<p>Follow <b>@AngryBirdDC_</b> on X for mint date reveal &amp; brand updates.</p>
			</template>
			<a
				class="ew-btn"
				href="https://x.com/AngryBirdDC_"
				target="_blank"
				rel="noopener"
				>Follow the X</a
			>
			<button
				class="ew-btn ew-btn-secondary"
				@click="closeEarlyWorm"
			>
				Close
			</button>
		</div>
	</div>

	<!-- ===== Early Worm — Modal 3: saldo insuficiente ===== -->
	<div
		class="ew-overlay"
		:class="{ show: ewModal === 'fail' }"
	>
		<div
			class="ew-backdrop"
			@click="closeEarlyWorm"
		></div>
		<div class="ew-modal">
			<button
				class="ew-close"
				aria-label="Fechar"
				@click="closeEarlyWorm"
			>
				✕
			</button>
			<div class="ew-icon-row"><span class="dot"></span><span>Access Not Granted</span></div>
			<h2>Not Enough Quill</h2>
			<p>This wallet doesn't hold the minimum <b>$QUILL</b> required to be added to the Early Worm whitelist.</p>
			<p>Grab some $QUILL &amp; reconnect your wallet to complete the Early Worm confirmation process.</p>
			<a
				class="ew-btn ew-btn-buy"
				href="https://jup.ag/tokens/MtSpDW2iXzFGFPrMmCj3QDBmYYmnQJG21PF8ibdboop"
				target="_blank"
				rel="noopener"
			>
				<img
					class="quill-icon-buy-img"
					src="../../assets/images/QUILL-ICON-WHITE.png"
					alt=""
				/>
				$QUILL available via Jupiter Exchange
				<svg
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
				>
					<path d="M7 17L17 7M17 7H8M17 7v9" />
				</svg>
			</a>
		</div>
	</div>
</template>

<script>
import BurgerMenu from '@/components/header/BurgerMenu';

const CIRCUMFERENCE = 326.7;
const VERIFY_DURATION = 2600;

export default {
	name: 'HeaderComp',
	components: { BurgerMenu },
	data() {
		return {
			isScrolled: false,
			navOpen: false,
			earlyWormBusy: false,
			ewModal: null, // null | 'intro' | 'success' | 'fail'
			// Kept separate from ewModal (not reset on close) so the success
			// modal's text doesn't flip mid fade-out.
			ewAlreadyRegistered: false,
			connectLabel: 'Connect Wallet',
			portalActive: false,
			portalExpand: false,
			portalExiting: false,
			portalGranted: false,
			portalPercent: 0,
			portalDashOffset: CIRCUMFERENCE,
			portalX: '50%',
			portalY: '50%',
		};
	},
	computed: {
		isManifesto() {
			return this.$route.path === '/manifesto';
		},
		portalStyle() {
			return { '--px': this.portalX, '--py': this.portalY };
		},
	},
	mounted() {
		window.addEventListener('scroll', this.onScroll);
		document.addEventListener('keydown', this.onKeydown);
	},
	beforeUnmount() {
		window.removeEventListener('scroll', this.onScroll);
		document.removeEventListener('keydown', this.onKeydown);
	},
	methods: {
		onScroll() {
			this.isScrolled = window.scrollY > 20;
		},
		goHome() {
			this.navOpen = false;
			if (this.$route.path !== '/') this.$router.push('/');
		},
		// Same provider-detection pattern Q's Secret Club uses (no
		// @solana/wallet-adapter there either — it reads the extension's
		// injected window object directly).
		getPhantomProvider() {
			if (window.phantom && window.phantom.solana && window.phantom.solana.isPhantom) return window.phantom.solana;
			if (window.solana && window.solana.isPhantom) return window.solana;
			return null;
		},
		getSolflareProvider() {
			if (window.solflare && window.solflare.isSolflare) return window.solflare;
			return null;
		},
		// EARLY WORM: connect only, no signature (decision recorded
		// 2026-09-11) — just reads the public key and asks the QSC backend
		// (reused for this separate abdc_whitelist table) whether it holds
		// >= the required QUILL balance. Path is same-origin in production
		// (DigitalOcean routes /qs-secret-club/* to that backend on this
		// same domain); locally it's proxied by vue.config.js.
		openEarlyWorm() {
			this.navOpen = false;
			this.connectLabel = 'Connect Wallet';
			this.ewModal = 'intro';
		},
		closeEarlyWorm() {
			this.ewModal = null;
		},
		onKeydown(e) {
			if (e.key === 'Escape') this.closeEarlyWorm();
		},
		async onConnectWallet() {
			if (this.earlyWormBusy) return;

			const provider = this.getPhantomProvider() || this.getSolflareProvider();
			if (!provider) {
				alert('No Solana wallet found. Install Phantom or Solflare and try again.');
				return;
			}

			this.earlyWormBusy = true;
			this.connectLabel = 'Connecting…';

			try {
				const connectResp = await provider.connect();
				const walletAddress = ((connectResp && connectResp.publicKey) || provider.publicKey).toString();
				if (!walletAddress) throw new Error('Could not read the wallet address.');

				this.connectLabel = 'Checking…';
				const res = await fetch('/qs-secret-club/api/abdc/early-worm', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ wallet_address: walletAddress }),
				});
				const data = await res.json();
				if (!res.ok) throw new Error(data.error || 'Something went wrong.');

				// Same close → reopen timing as the approved prototype.
				this.ewModal = null;
				setTimeout(() => {
					this.ewAlreadyRegistered = Boolean(data.alreadyRegistered);
					this.ewModal = data.qualifies ? 'success' : 'fail';
				}, 350);
			} catch (err) {
				this.connectLabel = 'Error — Retry';
			} finally {
				this.earlyWormBusy = false;
			}
		},
		onSecretClubClick(e) {
			e.preventDefault();
			this.navOpen = false;
			const destination = e.currentTarget.href;
			const rect = e.currentTarget.getBoundingClientRect();
			this.portalX = rect.left + rect.width / 2 + 'px';
			this.portalY = rect.top + rect.height / 2 + 'px';

			this.portalGranted = false;
			this.portalDashOffset = CIRCUMFERENCE;
			this.portalPercent = 0;
			this.portalExpand = false;
			this.portalExiting = false;
			this.portalActive = true;

			const start = performance.now();
			const tick = (now) => {
				const elapsed = now - start;
				const t = Math.min(elapsed / VERIFY_DURATION, 1);
				this.portalPercent = Math.round(t * 100);
				this.portalDashOffset = CIRCUMFERENCE * (1 - t);

				if (t < 1) {
					requestAnimationFrame(tick);
				} else {
					this.portalGranted = true;
					setTimeout(() => {
						this.portalExiting = true;
						this.portalExpand = true;
					}, 550);
					setTimeout(() => {
						window.location.href = destination;
					}, 550 + 900);
				}
			};
			requestAnimationFrame(tick);
		},
	},
};
</script>

<style scoped>
header {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	z-index: 40;
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 28px 48px;
	transition:
		padding 0.4s ease,
		background 0.4s ease,
		backdrop-filter 0.4s ease,
		border-color 0.4s ease;
	border-bottom: 1px solid transparent;
}
header.scrolled {
	padding: 16px 48px;
	background: rgba(8, 9, 10, 0.55);
	backdrop-filter: blur(14px) saturate(140%);
	border-bottom: 1px solid var(--line);
}
.logo-mark {
	background: none;
	border: none;
	cursor: pointer;
	padding: 0;
	display: block;
}
.logo-mark img {
	height: 64px;
	width: auto;
	display: block;
	opacity: 0.95;
	transition: height 0.4s ease;
}
header.scrolled .logo-mark img {
	height: 52px;
}

nav {
	display: flex;
	align-items: center;
	gap: 40px;
}
.nav-link {
	font-size: 13px;
	letter-spacing: 0.14em;
	color: var(--ink-dim);
	text-decoration: none;
	text-transform: uppercase;
	position: relative;
	padding-bottom: 4px;
	transition: color 0.3s ease;
	font-weight: 500;
	background: none;
	border: none;
	cursor: pointer;
	font-family: inherit;
}
.nav-link.active {
	color: var(--ink);
}
.nav-link::after {
	content: '';
	position: absolute;
	left: 0;
	bottom: 0;
	width: 0;
	height: 1px;
	background: var(--ink);
	transition: width 0.35s cubic-bezier(0.16, 0.8, 0.24, 1);
}
.nav-link.active::after {
	width: 100%;
	background: var(--blood-bright);
}
.nav-link:hover {
	color: var(--ink);
}
.nav-link:hover::after {
	width: 100%;
}
.early-worm-link {
	color: var(--blood-bright);
}
.early-worm-link::after {
	background: var(--blood-bright);
}
.early-worm-link:hover {
	color: var(--blood-bright);
}

.nav-socials {
	display: flex;
	align-items: center;
	gap: 20px;
	padding-left: 20px;
	border-left: 1px solid var(--line);
}
.nav-socials a {
	display: flex;
	opacity: 0.75;
	transition:
		opacity 0.3s ease,
		transform 0.3s ease;
}
.nav-socials .social-disabled {
	display: flex;
	opacity: 0.4;
	cursor: default;
}
.nav-socials a:hover {
	opacity: 1;
	transform: translateY(-2px);
}
.nav-socials img,
.nav-socials svg {
	height: 18px;
	width: 18px;
}

/* ---------- Portal transition (Secret Club) ---------- */
.portal-overlay {
	position: fixed;
	inset: 0;
	z-index: 200;
	pointer-events: none;
	opacity: 0;
	visibility: hidden;
}
.portal-overlay.active {
	pointer-events: all;
	opacity: 1;
	visibility: visible;
}

.portal-backdrop {
	position: fixed;
	inset: 0;
	background: rgba(4, 4, 5, 0.88);
	backdrop-filter: blur(6px);
	opacity: 0;
	transition: opacity 0.4s ease;
}
.portal-overlay.active .portal-backdrop {
	opacity: 1;
}

.portal-content {
	position: fixed;
	inset: 0;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 18px;
	opacity: 0;
	transform: scale(0.92);
	transition:
		opacity 0.5s ease,
		transform 0.5s ease;
	transition-delay: 0.15s;
}
.portal-overlay.active .portal-content {
	opacity: 1;
	transform: scale(1);
}
.portal-overlay.exiting .portal-content {
	opacity: 0;
	transition-delay: 0s;
}

.portal-ring-wrap {
	position: relative;
	width: 120px;
	height: 120px;
	display: flex;
	align-items: center;
	justify-content: center;
}
.portal-ring-svg {
	position: absolute;
	inset: 0;
	width: 120px;
	height: 120px;
	transform: rotate(-90deg);
}
.ring-track {
	fill: none;
	stroke: rgba(243, 241, 236, 0.14);
	stroke-width: 2.5;
}
.ring-progress {
	fill: none;
	stroke: var(--ink);
	stroke-width: 2.5;
	stroke-linecap: round;
	stroke-dasharray: 326.7;
	stroke-dashoffset: 326.7;
	transition: stroke 0.3s ease;
}
.ring-progress.granted {
	stroke: var(--blood-bright);
	filter: drop-shadow(0 0 6px rgba(163, 32, 32, 0.7));
}

.portal-percent {
	position: relative;
	font-family: 'JetBrains Mono', monospace;
	font-size: 20px;
	letter-spacing: 0.02em;
	color: var(--ink);
	font-weight: 500;
}

.portal-status {
	margin-top: 14px;
	font-family: 'JetBrains Mono', monospace;
	font-size: 11px;
	letter-spacing: 0.28em;
	color: var(--ink-dim);
	text-transform: uppercase;
	transition: color 0.3s ease;
}
.portal-status.granted {
	color: var(--blood-bright);
	font-weight: 500;
}

.portal-circle {
	position: fixed;
	top: var(--py, 50%);
	left: var(--px, 50%);
	width: 1px;
	height: 1px;
	background: var(--blood-bright);
	border-radius: 50%;
	transform: translate(-50%, -50%) scale(0);
	transition: transform 0.9s cubic-bezier(0.65, 0, 0.35, 1);
}
.portal-overlay.expand .portal-circle {
	transform: translate(-50%, -50%) scale(3200);
}

/* ---------- Early Worm modals ---------- */
.ew-overlay {
	position: fixed;
	inset: 0;
	z-index: 300;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px;
	opacity: 0;
	visibility: hidden;
	transition:
		opacity 0.3s ease,
		visibility 0.3s ease;
}
.ew-overlay.show {
	opacity: 1;
	visibility: visible;
}

.ew-backdrop {
	position: absolute;
	inset: 0;
	background: rgba(4, 4, 5, 0.86);
	backdrop-filter: blur(8px);
}

.ew-modal {
	position: relative;
	width: 100%;
	max-width: 440px;
	background: #0d0d0e;
	border: 1px solid rgba(243, 241, 236, 0.12);
	border-radius: 4px;
	padding: 36px 32px 32px;
	transform: translateY(16px) scale(0.98);
	opacity: 0;
	transition:
		transform 0.35s cubic-bezier(0.16, 0.8, 0.24, 1),
		opacity 0.35s ease;
}
.ew-overlay.show .ew-modal {
	transform: translateY(0) scale(1);
	opacity: 1;
}

.ew-modal::before {
	content: '';
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	height: 2px;
	background: var(--blood-bright);
}

.ew-close {
	position: absolute;
	top: 16px;
	right: 16px;
	width: 28px;
	height: 28px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: none;
	border: none;
	cursor: pointer;
	color: var(--ink-dim);
	font-size: 18px;
	transition: color 0.2s ease;
}
.ew-close:hover {
	color: var(--ink);
}

.ew-icon-row {
	display: flex;
	align-items: center;
	gap: 10px;
	margin-bottom: 18px;
}
.ew-icon-row .dot {
	width: 6px;
	height: 6px;
	border-radius: 50%;
	background: var(--blood-bright);
	box-shadow: 0 0 8px 2px rgba(163, 32, 32, 0.6);
}
.ew-icon-row span {
	font-family: 'JetBrains Mono', monospace;
	font-size: 10px;
	letter-spacing: 0.22em;
	color: var(--ink-dim);
	text-transform: uppercase;
}

.ew-modal h2 {
	font-family: 'Dead Revolution', 'Georgia', serif;
	/* the prototype relied on the browser's default bold for h2;
	Tailwind's preflight resets it, so it's set explicitly here */
	font-weight: 700;
	font-size: 1.7rem;
	letter-spacing: 0.02em;
	text-transform: uppercase;
	color: var(--ink);
	margin-bottom: 16px;
	line-height: 1.15;
}

.ew-modal p {
	font-size: 14px;
	line-height: 1.65;
	color: var(--ink-dim);
	margin-bottom: 14px;
}
.ew-modal p b {
	color: var(--ink);
	font-weight: 600;
}

.ew-fineprint {
	font-size: 11.5px !important;
	color: rgba(243, 241, 236, 0.42) !important;
	margin-top: 10px !important;
	margin-bottom: 0 !important;
}
.ew-fineprint:first-of-type {
	border-top: 1px solid rgba(243, 241, 236, 0.1);
	padding-top: 16px;
	margin-top: 20px !important;
}
.ew-fineprint + .ew-fineprint {
	margin-top: 10px !important;
}

.ew-icon-box {
	width: 56px;
	height: 56px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(163, 32, 32, 0.12);
	border: 1px solid rgba(163, 32, 32, 0.4);
	border-radius: 4px;
	margin-bottom: 20px;
}
.ew-icon-box svg {
	width: 26px;
	height: 26px;
	stroke: var(--blood-bright);
}

/* "Already whitelisted" variant of the success modal — gold accent
   instead of --blood-bright (reference-whitelisted-modal.html). */
.ew-modal.whitelisted::before {
	background: #e8c454;
}
.ew-modal.whitelisted .ew-icon-box {
	background: rgba(201, 162, 39, 0.12);
	border: 1px solid #e8c454;
}
.ew-modal.whitelisted .ew-icon-box svg {
	stroke: #e8c454;
}
.ew-modal.whitelisted .ew-icon-row span {
	color: #e8c454;
	font-weight: 600;
}

.secured-chip {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 6px 14px;
	border-radius: 100px;
	border: 1px solid #e8c454;
	background: rgba(201, 162, 39, 0.08);
	color: #e8c454;
	font-family: 'JetBrains Mono', monospace;
	font-size: 11px;
	letter-spacing: 1.2px;
	font-weight: 600;
	margin-bottom: 18px;
}
.secured-chip svg {
	width: 13px;
	height: 13px;
}

.ew-btn.ew-btn-secondary {
	background: transparent;
	color: var(--ink-dim);
	border: 1px solid rgba(243, 241, 236, 0.18);
	margin-top: 10px;
}
.ew-btn.ew-btn-secondary:hover {
	background: rgba(243, 241, 236, 0.06);
	color: var(--ink);
}

.ew-btn {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 10px;
	width: 100%;
	margin-top: 16px;
	padding: 14px 20px;
	background: var(--ink);
	color: #0a0a0a;
	border: none;
	border-radius: 2px;
	font-family: 'Poppins', sans-serif;
	font-size: 13px;
	font-weight: 600;
	letter-spacing: 0.12em;
	text-transform: uppercase;
	cursor: pointer;
	transition:
		background 0.25s ease,
		transform 0.15s ease;
}
.ew-btn:hover {
	background: #fff;
	transform: translateY(-1px);
}
.ew-btn:disabled {
	opacity: 0.7;
	cursor: default;
	transform: none;
}

.ew-btn-buy {
	background: var(--blood-bright);
	color: var(--ink);
}
.ew-btn-buy:hover {
	background: #c22626;
}
.ew-btn-buy img {
	height: 16px;
	width: auto;
}
.ew-btn-buy svg {
	width: 13px;
	height: 13px;
	opacity: 0.85;
}

.quill-icon-buy-img {
	height: 18px;
	width: auto;
	flex-shrink: 0;
}

/* ---------- Mobile ---------- */
@media (max-width: 900px) {
	header {
		padding: 22px 24px;
	}
	header.scrolled {
		padding: 14px 24px;
	}
	nav {
		position: fixed;
		top: 0;
		right: 0;
		height: 100vh;
		width: min(78vw, 320px);
		background: rgba(8, 9, 10, 0.97);
		backdrop-filter: blur(20px);
		flex-direction: column;
		align-items: flex-start;
		justify-content: center;
		gap: 32px;
		padding: 48px;
		transform: translateX(100%);
		transition: transform 0.5s cubic-bezier(0.16, 0.8, 0.24, 1);
		border-left: 1px solid var(--line);
	}
	nav.open {
		transform: translateX(0);
	}
	.nav-socials {
		border-left: none;
		border-top: 1px solid var(--line);
		padding-left: 0;
		padding-top: 24px;
		width: 100%;
	}
}
</style>
